import { Injectable, Inject, NgZone, DOCUMENT } from '@angular/core';


@Injectable({
  providedIn: 'root'
})
export class InternalAccessibilityObserverService {
  private observer!: MutationObserver;
  private landmarkCounter = 0;
  private processTimeout: any;
  private started = false;
  private globalLandmarkLabels = new Map<string, number>();

  constructor(@Inject(DOCUMENT) private document: Document) { }

  public start(): void {
    // Guard: to run only once
    if (this.started) return;

    this.started = true;
    const body = this.document.body as HTMLElement;

    // Apply fixes to the current DOM which is already loaded. 
    this.applyFixesToTree(body);

    // Begin watching for future DOM changes to aplly fixes.
    this.initObserver();
  }

  private applyFixesToTree(root: HTMLElement): void {
    this.applyWcagFixes(root);
    root.querySelectorAll('*').forEach((el) => {
      if (el instanceof HTMLElement) {
        this.applyWcagFixes(el);
      }
    });
  }

  // Disconnect the observer to stop receiving mutations
  public stop(): void {
    if (this.observer) {
      try {
        this.observer.disconnect();
      } finally {
        this.observer = undefined;
      }
    }

    // Clear debounced work
    if (this.processTimeout) {
      clearTimeout(this.processTimeout);
      this.processTimeout = undefined;
    }

    // Reset started flag
    this.started = false;
  }

  private initObserver(): void {
    this.observer = new MutationObserver((mutations) => {
      let nodesAdded = false;

      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              this.applyWcagFixes(node);
              node.querySelectorAll('*').forEach((child) => {
                if (child instanceof HTMLElement) {
                  this.applyWcagFixes(child);
                }
              });
              nodesAdded = true;
            }
          });
        }
        else if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement && mutation.attributeName) {
          this.handleAttributeMutation(mutation.target, mutation.attributeName);
        }
      }

      if (nodesAdded) {
        clearTimeout(this.processTimeout);
        this.processTimeout = setTimeout(() => {
          this.ensureUniqueLandmarks(this.document.body);
        }, 300);
      }
    });

    this.observer.observe(this.document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-valuenow', 'aria-activedescendant', 'aria-controls', 'aria-level', 'aria-expanded']
    });
  }

  private handleAttributeMutation(target: HTMLElement, attr: string) {
    switch (attr) {
      case 'aria-valuenow':
        this.fixInvalidAriaValues(target);
        break;

      case 'aria-activedescendant':
        this.fixInvalidAriaActivedescendant(target);
        break;

      case 'aria-controls':
        this.fixInvalidAriaControls(target);
        break;

      case 'aria-level':
        const role = target.getAttribute('role') || '';
        if (role === 'menuitem' && target.hasAttribute('aria-level')) {
          target.removeAttribute('aria-level');
        }
        break;

      case 'aria-expanded':
        this.fixInvalidAriaExpanded(target);
        break;
    }
  }

  private applyWcagFixes(element: HTMLElement): void {
    this.fixTextAlternatives(element);
    this.fixInfoAndRelationships(element);
    this.fixKeyboardAccessibility(element);
    this.fixFocusOrder(element);
    this.fixHeadings(element);
    this.fixSensoryCharacteristics(element);
    this.fixMeaningfulSequence(element);
    this.fixPrimeNGComponents(element);
    this.fixGenericInputAttributes(element);
    this.fixPrimeNGDropdownRoles(element);
    this.fixNestedFocusableElements(element);
    this.fixListboxAccessibleNames(element);
    this.fixInvalidAriaMultiselectable(element);
    this.fixInteractiveElementsWithoutLabel(element);
    this.fixTextareaAccessibleName(element);
    this.fixNestedInteractiveControls(element);
    this.fixInvalidAriaRequired(element);
    this.fixScrollableContainers(element);
    this.fixProgressbarAccessibleName(element);
    this.fixTableHeadersAccessibility(element);
    this.fixEmptyListboxMessage(element);
    this.fixensureUniqueNavLandmarks(element);
    this.fixInvalidListboxRoles(element);
    this.fixMissingLandmarks(element);
    this.fixHeadingOrder(element);
    this.fixInvalidRoleOnLabels(element);
    this.fixInvalidFormRoles(element);
    this.fixInvalidTabindex(element);
    this.fixHeadingParentLandmark(element);
    this.fixInvalidToggleableContentRoles(element);
    this.fixEmptyHeadings(element);
    this.fixAriaLabelOnGenericElements(element);
    this.normalizeRowFocusTarget(element);
  }

  private normalizeRowFocusTarget(root: HTMLElement): void {
    root.querySelectorAll<HTMLElement>('tr.p-selectable-row[tabindex="0"]').forEach(tr => {
      const firstCell = tr.querySelector<HTMLElement>('td, th');
      if (!firstCell) return;
      tr.setAttribute('tabindex', '-1');
      if (!firstCell.hasAttribute('tabindex')) {
        firstCell.setAttribute('tabindex', '0');
      }
    });

    const focusTargetSelectors = [
      'tr.p-selectable-row[tabindex="0"]',
      '.p-selectable-row.p-focus',
      '.p-selectable-row[tabindex="0"].p-highlight',
      '.p-selectable-row',
      'button',
      '.p-button'
    ].join(',');

    const applyOutline = (el: HTMLElement) => {
      el.style.outline = '2px solid transparent';
      el.style.outlineOffset = '2px';
    };

    const focusOutlineKey = '__focusOutlineBindings__';
    if ((root as any)[focusOutlineKey]) return;

    root.addEventListener('focusin', (e: FocusEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const match = t.closest(focusTargetSelectors) as HTMLElement | null;
      if (!match) return;
      applyOutline(match);
    }, true);

    (root as any)[focusOutlineKey] = true;
  }

  // PrimeNG component accessibility fixes
  private fixPrimeNGComponents(el: HTMLElement): void {

    // p-radioButton
    if (el.classList.contains('p-radiobutton')) {
      const input = el.querySelector('input[type="radio"]');
      const labelText = el.getAttribute('data-label') || 'radio option';

      if (input) {
        if (!input.hasAttribute('aria-label')) {
          input.setAttribute('aria-label', labelText);
        }
        if (!input.hasAttribute('role')) {
          input.setAttribute('role', 'radio');
        }
        if (!input.hasAttribute('aria-checked')) {
          input.setAttribute('aria-checked', (input as HTMLInputElement).checked ? 'true' : 'false');
        }
        input.addEventListener('change', () => {
          input.setAttribute('aria-checked', (input as HTMLInputElement).checked ? 'true' : 'false');
        });
      }

      if (!el.hasAttribute('tabindex') && !el.classList.contains('p-disabled') && !(input as HTMLInputElement)?.disabled) {
        el.setAttribute('tabindex', '0');
      }
    }

    // p-checkbox
    if (el.classList.contains('p-checkbox')) {
      const box = el.querySelector('.p-checkbox-box');
      const input = el.querySelector('input[type="checkbox"]');
      const parentInteractive = el.closest('[role="option"], [role="row"], [role="button"], [role="link"]');
      const isNestedInInteractive = parentInteractive && parentInteractive !== el;
      if (box) {
        if (!isNestedInInteractive) {
          if (!box.hasAttribute('role')) {
            box.setAttribute('role', 'checkbox');
          }
          if (!box.hasAttribute('tabindex')) {
            box.setAttribute('tabindex', '0');
          }
          if (!box.hasAttribute('aria-checked')) {
            box.setAttribute('aria-checked', (input as HTMLInputElement)?.checked ? 'true' : 'false');
          }
        } else {
          box.removeAttribute('role');
          box.removeAttribute('tabindex');
          box.removeAttribute('aria-checked');
        }
        if (!box.hasAttribute('aria-label')) {
          const label = input?.getAttribute('aria-label') || 'checkbox option';
          box.setAttribute('aria-label', label);
        }
      }
    }

    //p-multislect 
    if (el.classList.contains('p-multiselect')) {
      if (!el.hasAttribute('role')) {
        el.setAttribute('role', 'combobox');
      }
      if (!el.hasAttribute('aria-haspopup')) {
        el.setAttribute('aria-haspopup', 'listbox');
      }
      if (!el.hasAttribute('aria-expanded')) {
        el.setAttribute('aria-expanded', 'false');
      }
      if (!el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', 'Filter by routing type');
      }
    }

    // p-calendar
if (el.classList.contains('p-calendar')) {
      const input = el.querySelector('input[type="text"]');
      const popup = el.querySelector('.p-datepicker');

      if (input) {
        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-haspopup', 'dialog');
        input.setAttribute('aria-expanded', 'false');
        input.setAttribute('aria-label', 'Date picker');

        if (popup && !popup.id) {
          popup.id = 'calendar-panel-' + Math.random().toString(36).substring(2, 8);
        }

        if (popup?.id) {
          input.setAttribute('aria-controls', popup.id);
        }
      }
    }

    // p-autocomplete
    if (el.classList.contains('p-autocomplete')) {
      const input = el.querySelector('input[type="text"]');
      if (input) {
        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-haspopup', 'listbox');
        input.setAttribute('aria-expanded', 'false');
        input.setAttribute('aria-label', 'Search suggestions');
      }
    }

    // p-datepicker
    if (el.classList.contains('p-datepicker')) {
      const input = el.querySelector('input[type="text"]');
      if (input) {
        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-haspopup', 'dialog');
        input.setAttribute('aria-expanded', 'false');
        input.setAttribute('aria-label', 'Date picker');
      }
    }


    // p-dropdown
    if (el.classList.contains('p-dropdown')) {
      if (!el.hasAttribute('role')) {
        el.setAttribute('role', 'combobox');
      }
      if (!el.hasAttribute('aria-haspopup')) {
        el.setAttribute('aria-haspopup', 'listbox');
      }
      if (!el.hasAttribute('aria-expanded')) {
        el.setAttribute('aria-expanded', 'false');
      }
      if (!el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', 'dropdown menu');
      }
    }

    // p-dropdown used in paginator
    if (el.classList.contains('p-dropdown') && el.classList.contains('p-paginator-rpp-options')) {
      const input = el.querySelector('input[type="text"]');
      const trigger = el.querySelector('.p-dropdown-trigger');
      const label = el.querySelector('.p-dropdown-label');

      if (input) {
        if (!input.hasAttribute('role')) {
          input.setAttribute('role', 'combobox');
        }
        if (!input.hasAttribute('aria-haspopup')) {
          input.setAttribute('aria-haspopup', 'listbox');
        }
        if (!input.hasAttribute('aria-expanded')) {
          input.setAttribute('aria-expanded', 'false');
        }
        if (!input.hasAttribute('aria-label')) {
          input.setAttribute('aria-label', 'Items per page selector');
        }
        if (!input.hasAttribute('disabled') && el.classList.contains('p-disabled')) {
          input.setAttribute('disabled', '');
        }
      }

      if (trigger && !trigger.hasAttribute('aria-label')) {
        trigger.setAttribute('aria-label', 'Open items per page dropdown');
      }

      if (label && !label.hasAttribute('aria-label')) {
        label.setAttribute('aria-label', 'Selected items per page');
      }
    }

    // p-paginator navigation buttons
    if (
      el.tagName === 'BUTTON' &&
      el.classList.contains('p-paginator-element')
    ) {
      const labelMap = {
        'p-paginator-first': 'First page',
        'p-paginator-prev': 'Previous page',
        'p-paginator-next': 'Next page',
        'p-paginator-last': 'Last page'
      };
      type LabelMapKey = keyof typeof labelMap;
      for (const cls in labelMap) {
        if (el.classList.contains(cls) && !el.hasAttribute('aria-label')) {
          el.setAttribute('aria-label', labelMap[cls as LabelMapKey]);
        }
      }

      if (!el.hasAttribute('role')) {
        el.setAttribute('role', 'button');
      }

      if (!el.hasAttribute('aria-disabled')) {
        const isDisabled = el.hasAttribute('disabled') || el.classList.contains('p-disabled');
        el.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
      }

      if (!el.hasAttribute('tabindex') && !el.hasAttribute('disabled')) {
        el.setAttribute('tabindex', '0');
      }
    }

    // readonly input[type="text"]
    if (
      el.tagName === 'INPUT' &&
      el.getAttribute('type') === 'text' &&
      el.getAttribute('readonly') === 'readonly'
    ) {
      el.setAttribute('role', 'combobox');

      if (!el.hasAttribute('aria-haspopup')) {
        el.setAttribute('aria-haspopup', 'listbox');
      }
      if (!el.hasAttribute('aria-expanded')) {
        el.setAttribute('aria-expanded', 'false');
      }
      if (!el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', 'Selection input');
      }
      if (!el.hasAttribute('tabindex')) {
        el.setAttribute('tabindex', '0');
      }

      if (!el.hasAttribute('aria-controls')) {
        const isMultiSelect = !!el.closest('.p-multiselect');
        const panelSelector = isMultiSelect
          ? '.p-multiselect-panel ul[role="listbox"]'
          : '.p-dropdown-panel ul[role="listbox"], .p-autocomplete-panel ul[role="listbox"]';

        let listbox = this.document.querySelector(panelSelector) as HTMLElement;

        if (!listbox) {
          listbox = this.document.querySelector('ul[role="listbox"]') as HTMLElement;
        }

        if (listbox) {
          const listboxId = listbox.id || `listbox-${Math.random().toString(36).substring(2, 8)}`;
          listbox.id = listboxId;
          el.setAttribute('aria-controls', listboxId);
        } else {
          const fallbackId = `listbox-fallback-${el.id}`;
          el.setAttribute('aria-controls', fallbackId);
        }
      }
    }

    // p-splitbutton menu button
    if (
      el.tagName === 'BUTTON' &&
      el.classList.contains('p-splitbutton-menubutton')
    ) {
      if (el.hasAttribute('aria-aria-haspopup')) {
        el.removeAttribute('aria-aria-haspopup');
      }
      if (!el.hasAttribute('aria-haspopup')) {
        el.setAttribute('aria-haspopup', 'menu');
      }
      if (!el.hasAttribute('aria-expanded')) {
        el.setAttribute('aria-expanded', 'false');
      }
      if (!el.hasAttribute('aria-label') || el.getAttribute('aria-label') === 'action') {
        el.setAttribute('aria-label', 'Open split button menu');
      }
      if (!el.hasAttribute('role')) {
        el.setAttribute('role', 'button');
      }
      if (!el.hasAttribute('aria-disabled')) {
        const isDisabled = el.hasAttribute('disabled') || el.classList.contains('p-disabled');
        el.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
      }
      if (!el.hasAttribute('tabindex') && !el.hasAttribute('disabled')) {
        el.setAttribute('tabindex', '0');
      }
    }

    // p-table / p-datatable
    if (el.classList.contains('p-table') || el.classList.contains('p-datatable')) {
      const table = el.querySelector('table');
      if (table && !table.hasAttribute('role')) {
        table.setAttribute('role', 'table');
      }
      const headers = table?.querySelectorAll('th');
      headers?.forEach(th => {
        if (!th.hasAttribute('scope')) {
          th.setAttribute('scope', 'col');
        }
      });
    }

    // p-inputnumber 
    if (el.classList.contains('p-inputnumber-input') && el.tagName === 'INPUT') {
      const inputElement = el as HTMLInputElement;
      if (!inputElement.hasAttribute('role')) inputElement.setAttribute('role', 'spinbutton');
      const val = inputElement.getAttribute('aria-valuenow');
      if (val === 'null' || val === '') {
        inputElement.setAttribute('aria-valuenow', inputElement.value || '0');
      }
    }

    // p-button
    if (el.classList.contains('p-button') && el.tagName === 'BUTTON') {
      const label = el.getAttribute('ng-reflect-label');
      if (!el.hasAttribute('aria-label') && label) {
        el.setAttribute('aria-label', label);
      }
    }

    if (el.classList.contains('p-button-white') && el.getAttribute('role') === 'checkbox') {
      if (!el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', 'Toggle option');
      }
    }

    // p-inputswitch
    if (el.tagName === 'P-INPUTSWITCH' || el.classList.contains('p-inputswitch')) {
      const input = el.querySelector('input[type="checkbox"]') as HTMLInputElement | null;

      if (input) {
        if (!input.hasAttribute('role')) {
          input.setAttribute('role', 'switch');
        }
        if (!input.hasAttribute('aria-checked')) {
          input.setAttribute('aria-checked', input.checked ? 'true' : 'false');
        }
        if (!input.hasAttribute('aria-label') && !input.hasAttribute('aria-labelledby')) {
          input.setAttribute('aria-label', 'Toggle option');
        }
        input.addEventListener('keydown', (event: KeyboardEvent) => {
          if (event.key === ' ' || event.key === 'Enter') {
            input.click();
            input.setAttribute('aria-checked', input.checked ? 'true' : 'false');
            event.preventDefault();
          }
        });
      }
    }

    // p-multiselect listbox
    if (el.tagName === 'UL' && el.getAttribute('role') === 'listbox') {
      if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
        el.setAttribute('aria-label', 'Available options');
      }
    }

    // Fix listbox child roles
    if (el.tagName === 'LI' && el.closest('ul[role="listbox"]')) {
      if (el.classList.contains('p-multiselect-item') && !el.hasAttribute('role')) {
        el.setAttribute('role', 'option');
      }
      if (el.classList.contains('p-multiselect-item-group') && !el.hasAttribute('role')) {
        el.setAttribute('role', 'group');
      }
    }

    // p-tabpanel
    if (el.tagName === 'DL') {
      const childDLs = el.querySelectorAll(':scope > dl');
      childDLs.forEach(child => {
        const wrapperDD = document.createElement('dd');
        el.insertBefore(wrapperDD, child);
        wrapperDD.appendChild(child);
      });
    }

    // p-overlayPanel Dialog + p-confirmDialog Alert Dialog fix
    if (el.getAttribute('role') === 'dialog' || el.getAttribute('role') === 'alertdialog') {
      if (!el.hasAttribute('aria-modal')) {
        el.setAttribute('aria-modal', 'true');
      }
      const labelledById = (el.getAttribute('aria-labelledby') || '').trim();
      let labelledByEl: HTMLElement | null = null;
      let labelledByHasText = false;

      if (labelledById) {
        labelledByEl = this.document.getElementById(labelledById) as HTMLElement | null;
        const text = labelledByEl?.textContent?.trim() || '';
        labelledByHasText = text.length > 0;
      }

      if (!labelledById || !labelledByEl || !labelledByHasText) {
        const heading = el.querySelector<HTMLElement>('h1, h2, h3, .p-dialog-title');
        const headingText = heading?.textContent?.trim() || '';

        if (heading && headingText) {
          if (!heading.id) {
            heading.id = `dialog-heading-${Math.random().toString(36).substring(2, 8)}`;
          }
          el.setAttribute('aria-labelledby', heading.id);
          el.removeAttribute('aria-label');
        } else {
          el.setAttribute('aria-label', 'Dialog');
          el.removeAttribute('aria-labelledby');
        }
      }
    }
  }

  // Fix PrimeNG dropdown ARIA roles   
  private fixPrimeNGDropdownRoles(element: HTMLElement): void {
    const wrappers = element.querySelectorAll('.p-dropdown-items-wrapper');

    wrappers.forEach(wrapper => {
      if (wrapper.getAttribute('role') === 'listbox') {
        wrapper.removeAttribute('role');
        wrapper.setAttribute('role', 'presentation');
      }

      if (!wrapper.hasAttribute('tabindex')) {
        wrapper.setAttribute('tabindex', '0');
      }

      const ul = wrapper.querySelector('ul');
      if (ul && !ul.hasAttribute('role')) {
        ul.setAttribute('role', 'listbox');
      }

      const liElements = ul?.querySelectorAll('li') || [];
      liElements.forEach(li => {
        if (!li.hasAttribute('role')) {
          li.setAttribute('role', 'option');
        }
      });
    });
  }

  // Ensuring main landmark
  private ensureMainLandmark(): void {
    const hasMain = this.document.querySelector('main, [role="main"]');

    if (!hasMain) {

      const main = this.document.createElement('main');
      main.setAttribute('role', 'main');
      main.setAttribute('id', 'main-content');
      main.setAttribute('aria-label', 'Main content');
      const header = this.document.querySelector('header');
      if (header && header.parentNode) {
        header.parentNode.insertBefore(main, header.nextSibling);
      } else {
        this.document.body.insertBefore(main, this.document.body.firstChild);
      }
    }
  }

  private ensureLevelOneHeading(): void {
    const hasH1 = this.document.querySelector('h1');
    if (!hasH1) {
      const h1 = this.document.createElement('h1');
      h1.textContent = 'Main Heading';
      h1.setAttribute('id', 'main-heading');
      h1.setAttribute('class', 'sr-only');
      h1.setAttribute('tabindex', '-1');
      h1.setAttribute('aria-hidden', 'false');

      const main = this.document.querySelector('main') || this.document.body;
      main.insertBefore(h1, main.firstChild);
    }
  }

  // Ensuring Unique Navigation Landmarks
  private fixensureUniqueNavLandmarks(root: HTMLElement): void {
    const navs = root.querySelectorAll('nav[role="navigation"]');
    navs.forEach((nav, index) => {
      const el = nav as HTMLElement;
      const hasLabel = el.hasAttribute('aria-label') || el.hasAttribute('aria-labelledby') || el.hasAttribute('title');

      if (!hasLabel) {
        const label = index === 0 ? 'Primary navigation' : `Navigation section ${index + 1}`;
        el.setAttribute('aria-label', label);
      }
    });
  }

  //PrimeNG accessible name fix and empty message fix
  private fixListboxAccessibleNames(element: HTMLElement): void {
    const listboxes = element.querySelectorAll('[role="listbox"]');
    listboxes.forEach(listbox => {
      const hasAriaLabel = listbox.hasAttribute('aria-label') && (listbox.getAttribute('aria-label') || '').trim() !== '';
      const hasAriaLabelledBy = listbox.hasAttribute('aria-labelledby') && (listbox.getAttribute('aria-labelledby') || '').trim() !== '';

      if (!hasAriaLabel && !hasAriaLabelledBy) {
        listbox.setAttribute('aria-label', 'Selectable list of options');
      }

      const liItems = listbox.querySelectorAll('li');
      liItems.forEach(li => {
        const isGroup = li.classList.contains('p-multiselect-item-group');
        const isEmptyMsg = li.classList.contains('p-listbox-empty-message');

        if (!isGroup && !isEmptyMsg) {
          if (li.getAttribute('role') !== 'option') {
            li.setAttribute('role', 'option');
          }

          if (li.hasAttribute('aria-selected')) {
            const v = (li.getAttribute('aria-selected') || '').toLowerCase();
            if (v !== 'true' && v !== 'false') {
              li.setAttribute('aria-selected', 'false');
            }
          }
        }

        if (isGroup && li.getAttribute('role') !== 'presentation') {
          li.setAttribute('role', 'presentation');
        }
      });
    });
  }

  //Fix for interactive elements (buttons, spans with role="button", links) to have an accessible name.
  private fixInteractiveElementsWithoutLabel(element: HTMLElement): void {
    const interactiveElements = element.querySelectorAll('button, [role="button"], a[role="button"], span[role="button"]');

    interactiveElements.forEach(el => {
      const htmlEl = el as HTMLElement;
      const hasText = htmlEl.textContent?.trim() !== '';
      const hasAriaLabel = htmlEl.hasAttribute('aria-label') && htmlEl.getAttribute('aria-label')?.trim() !== '';
      const hasAriaLabelledBy = htmlEl.hasAttribute('aria-labelledby') && htmlEl.getAttribute('aria-labelledby')?.trim() !== '';
      const hasTitle = htmlEl.hasAttribute('title') && htmlEl.getAttribute('title')?.trim() !== '';

      if (!hasText && !hasAriaLabel && !hasAriaLabelledBy && !hasTitle) {
        htmlEl.setAttribute('aria-label', 'action');
      }
    });
  }

  //Fix all textarea elements to have an accessible name.     
  private fixTextareaAccessibleName(element: HTMLElement): void {
    const textareas = element.querySelectorAll('textarea');
    textareas.forEach(textarea => {
      const htmlTextarea = textarea as HTMLElement;
      const hasAriaLabel = htmlTextarea.hasAttribute('aria-label') && htmlTextarea.getAttribute('aria-label')?.trim() !== '';
      const hasAriaLabelledBy = htmlTextarea.hasAttribute('aria-labelledby') && htmlTextarea.getAttribute('aria-labelledby')?.trim() !== '';
      const hasTitle = htmlTextarea.hasAttribute('title') && htmlTextarea.getAttribute('title')?.trim() !== '';
      const hasPlaceholder = htmlTextarea.hasAttribute('placeholder') && htmlTextarea.getAttribute('placeholder')?.trim() !== '';

      if (!hasAriaLabel && !hasAriaLabelledBy && !hasTitle && !hasPlaceholder) {
        const name = htmlTextarea.getAttribute('formcontrolname') || htmlTextarea.getAttribute('name') || 'textarea field';
        htmlTextarea.setAttribute('aria-label', name);
      }
    });
  }

  private fixNestedFocusableElements(element: HTMLElement): void {
    const listboxItems = element.querySelectorAll('li[role="option"]');
    listboxItems.forEach(item => {
      const focusableChildren = item.querySelectorAll('[tabindex="0"], [role="checkbox"]');
      focusableChildren.forEach(child => {
        child.removeAttribute('tabindex');
        if (child.getAttribute('role') === 'checkbox') {
          child.setAttribute('aria-hidden', 'true');
        }
      });
    });
    setTimeout(() => {
      const listboxItems = element.querySelectorAll('li[role="option"]');
      listboxItems.forEach(item => {
        const focusableChildren = item.querySelectorAll('[tabindex="0"], [role="checkbox"]');
        focusableChildren.forEach(child => {
          child.removeAttribute('tabindex');
          if (child.getAttribute('role') === 'checkbox') {
            child.setAttribute('aria-hidden', 'true');
          }
        });
      });
    }, 100);
  }

  //Generic input fixes
  private fixGenericInputAttributes(el: HTMLElement): void {
    if (el.tagName === 'INPUT') {
      const inputType = (el as HTMLInputElement).type;
      if (inputType === 'checkbox' || inputType === 'radio') return;


      const hasCalendarAria = el.getAttribute('role') === 'combobox' && el.getAttribute('aria-haspopup') === 'dialog';

      if (hasCalendarAria || el.closest('.p-calendar, .p-datepicker')) {
        if (el.id) {
          el.closest('.field')
            ?.querySelector(`label[for="${el.id}"]`)
            ?.removeAttribute('for');
        }

        return;
      }
      const name = el.getAttribute('formcontrolname') || el.getAttribute('name') || 'input field';
      if (!el.hasAttribute('aria-label')) el.setAttribute('aria-label', name);
      const id = el.id || `input-${Math.random().toString(36).substring(2, 8)}`;
      el.id = id;
      const label = el.closest('.field')?.querySelector('label');
      if (label) {
        if (!label.hasAttribute('for')) label.setAttribute('for', id);
        el.setAttribute('aria-labelledby', label.textContent?.trim() || name);
      }
    }
  }

  // General WCAG fixes
  private fixTextAlternatives(el: HTMLElement): void {
    if (el.tagName === 'IMG' && !el.hasAttribute('alt')) {
      el.setAttribute('alt', 'decorative');
    }
    if ((el.tagName === 'BUTTON' || el.tagName === 'A') && el.textContent?.trim() === '') {
      el.setAttribute('aria-label', 'action');
    }
  }

  private fixInvalidAriaValues(el: HTMLElement): void {
    if (el.hasAttribute('aria-valuenow')) {
      const role = el.getAttribute('role') || '';
      const allowedRoles = ['spinbutton', 'slider', 'progressbar'];
      if (!allowedRoles.includes(role)) {
        el.removeAttribute('aria-valuenow');
      } else {
        const val = el.getAttribute('aria-valuenow');
        if (val === 'null' || val === '') {
          const currentValue = (el as HTMLInputElement).value || '0';
          el.setAttribute('aria-valuenow', currentValue);
        }
      }
    }
  }

  //Fixes invalid ARIA `aria-activedescendant` attribute values
  private fixInvalidAriaActivedescendant(el: HTMLElement): void {
    if (el.hasAttribute('aria-activedescendant')) {
      const targetId = el.getAttribute('aria-activedescendant')!;
      if (!this.document.getElementById(targetId)) {
        el.removeAttribute('aria-activedescendant');
      }
    }
  }

  private fixInvalidAriaMultiselectable(el: HTMLElement): void {
    if (el.hasAttribute('aria-multiselectable')) {
      const value = el.getAttribute('aria-multiselectable')!;
      if (value !== 'true' && value !== 'false') {
        el.setAttribute('aria-multiselectable', 'true');
      }
    }
  }

  // Fix PrimeNG Listbox empty message without UI/behavior changes
  private fixEmptyListboxMessage(el: HTMLElement): void {
    if (el.classList.contains('p-listbox-empty-message')) {
      el.setAttribute('role', 'option');
      el.setAttribute('aria-selected', 'false');
      el.setAttribute('aria-disabled', 'true');
      el.setAttribute('tabindex', '-1');
      el.removeAttribute('aria-live');
      el.removeAttribute('aria-hidden');

      const hasText = (el.textContent || '').trim() !== '';
      const hasAriaLabel = el.hasAttribute('aria-label') && (el.getAttribute('aria-label') || '').trim() !== '';
      const hasTitle = el.hasAttribute('title') && (el.getAttribute('title') || '').trim() !== '';
      if (!hasText && !hasAriaLabel && !hasTitle) {
        el.setAttribute('aria-label', 'No options available');
      }
    }
  }

  private fixInvalidListboxRoles(el: HTMLElement): void {
    if (el.tagName === 'UL' && el.classList.contains('p-listbox-list')) {
      if (!el.hasAttribute('role')) {
        el.setAttribute('role', 'listbox');
      }
      return;
    }

    if (el.tagName === 'LI' && el.classList.contains('p-listbox-item')) {
      const parentIsListbox = !!el.closest('ul[role="listbox"]');

      if (parentIsListbox) {
        if (el.getAttribute('role') !== 'option') {
          el.setAttribute('role', 'option');
        }
        if (el.hasAttribute('aria-selected')) {
          const val = (el.getAttribute('aria-selected') || '').toLowerCase();
          if (val !== 'true' && val !== 'false') {
            el.setAttribute('aria-selected', 'false');
          }
        } else {
          const isSelected = el.classList.contains('p-highlight');
          el.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        }
        return;
      }

      el.removeAttribute('aria-selected');
      if (el.getAttribute('role') === 'option') {
        el.removeAttribute('role');
      }
    }
  }

  private fixInvalidAriaControls(el: HTMLElement): void {
    if (el.hasAttribute('aria-controls')) {
      const targetId = el.getAttribute('aria-controls')!;

      if (targetId.startsWith('listbox-fallback-')) {
        return;
      }

      if (!this.document.getElementById(targetId)) {
        el.removeAttribute('aria-controls');
      }
    }
  }

  private fixNestedInteractiveControls(el: HTMLElement): void {
    const anchorsWithRoleButton = el.querySelectorAll('a[role="button"]');
    anchorsWithRoleButton.forEach(anchor => {
      const innerBtn = anchor.querySelector('button');
      if (innerBtn) {
        // Remove interactive attributes from the outer anchor
        anchor.removeAttribute('role');
        anchor.removeAttribute('tabindex');
        anchor.removeAttribute('aria-expanded');
        anchor.removeAttribute('aria-controls');

        // Ensure inner button has an accessible name
        const hasLabel = innerBtn.hasAttribute('aria-label') && innerBtn.getAttribute('aria-label')!.trim() !== '';
        const hasTitle = innerBtn.hasAttribute('title') && innerBtn.getAttribute('title')!.trim() !== '';
        if (!hasLabel && !hasTitle) {
          const name = innerBtn.getAttribute('label') || innerBtn.textContent || 'action';
          innerBtn.setAttribute('aria-label', name.trim());
        }
      }
    });

    if (el.tagName === 'LI' && el.getAttribute('role') === 'option') {
      const nestedInteractive = el.querySelectorAll('button, [role="button"], a[role="button"]');
      if (nestedInteractive.length > 0) {
        el.removeAttribute('role');
        el.removeAttribute('tabindex');
        el.removeAttribute('aria-selected');
        el.removeAttribute('aria-label');
        el.removeAttribute('aria-labelledby');

        nestedInteractive.forEach(control => {
          const hasLabel = control.hasAttribute('aria-label') && control.getAttribute('aria-label')!.trim() !== '';
          const hasTitle = control.hasAttribute('title') && control.getAttribute('title')!.trim() !== '';
          if (!hasLabel && !hasTitle) {
            control.setAttribute('aria-label', 'action');
          }
        });

        const parentUl = el.closest('ul[role="listbox"]');
        if (parentUl) {
          parentUl.removeAttribute('role');
          parentUl.removeAttribute('aria-label');
          parentUl.removeAttribute('title');
        }
      }
    }
  }

  private fixEmptyHeadings(el: HTMLElement): void {
    if (el.matches('h1,h2,h3,h4,h5,h6') && el.textContent?.trim() === '') {
      // Add aria-label if no text
      if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
        el.setAttribute('aria-label', 'Section Heading'); // Use a meaningful label
      }
    }
  }

  // Fix WCAG issue: Ensure <th> elements have discernible text (icon-only and empty headers too).
  private fixTableHeadersAccessibility(element: HTMLElement): void {
    const headers = element.querySelectorAll('th[scope="col"]');
    headers.forEach(th => {
      const text = th.textContent?.trim();
      const ariaLabel = th.getAttribute('aria-label')?.trim();

      if (!text && !ariaLabel) {
        const span = document.createElement('span');
        span.className = 'sr-only';
        span.textContent = 'Column';
        th.appendChild(span);
      } else if (!text && ariaLabel) {
        const span = document.createElement('span');
        span.className = 'sr-only';
        span.textContent = ariaLabel;
        th.appendChild(span);
      }
    });
  }

  private fixAriaLabelOnGenericElements(el: HTMLElement): void {
    if (el.tagName === 'DIV' && el.hasAttribute('aria-label')) {
      const hasClick = typeof (el as any).onclick === 'function' || el.getAttribute('onclick') !== null;
      const isIconButtonLike =
        el.classList.contains('custom-icon') ||
        el.classList.contains('p-accordion-toggle-icon') ||
        el.getAttribute('data-test-id')?.includes('expand');

      if (hasClick || isIconButtonLike) {
        if (!el.hasAttribute('role')) el.setAttribute('role', 'button');
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');

        el.addEventListener('keydown', (event: KeyboardEvent) => {
          if (event.key === ' ' || event.key === 'Enter') {
            (el as HTMLElement).click();
            event.preventDefault();
          }
        });

        const label = el.getAttribute('aria-label')?.trim() || 'Expand';
        el.setAttribute('aria-label', label);
      } else {
        el.removeAttribute('aria-label');
        el.setAttribute('aria-hidden', 'true');
      }
    }
  }

  private fixInvalidAriaRequired(el: HTMLElement): void {
    if (el.hasAttribute('aria-required')) {
      const role = el.getAttribute('role') || '';
      const allowedRoles = ['radiogroup', 'combobox', 'textbox', 'listbox'];
      if (!allowedRoles.includes(role)) {
        const hasRadioButtons = el.querySelector('input[type="radio"]') !== null;
        if (hasRadioButtons) {
          el.setAttribute('role', 'radiogroup');
        } else {
          el.removeAttribute('aria-required');
        }
      }
    }
  }

  private fixInvalidAriaExpanded(el: HTMLElement): void {
    if (el.hasAttribute('aria-expanded')) {
      const role = el.getAttribute('role') || '';
      const allowedRoles = ['button', 'link', 'combobox', 'menuitem', 'treeitem'];
      if (!allowedRoles.includes(role)) {
        el.removeAttribute('aria-expanded');
      }
    }
  }

  private fixScrollableContainers(el: HTMLElement): void {
    const isScrollable = el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;

    if (isScrollable && (el.classList.contains('p-datatable-wrapper') || el.classList.contains('ng-sidebar--left'))) {
      if (!el.hasAttribute('tabindex')) {
        el.setAttribute('tabindex', '0');
      }

      if (!el.hasAttribute('role')) {
        el.setAttribute('role', 'region');
      }

      if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
        const baseLabel = el.classList.contains('p-datatable-wrapper')
          ? 'Scrollable data table'
          : 'Scrollable sidebar';

        this.landmarkCounter++;
        el.setAttribute('aria-label', `${baseLabel} ${this.landmarkCounter}`);
      }
    }
  }


  private fixProgressbarAccessibleName(el: HTMLElement): void {
    if (el.getAttribute('role') === 'progressbar') {
      const hasLabel = el.hasAttribute('aria-label') && el.getAttribute('aria-label')?.trim() !== '';
      const hasLabelledBy = el.hasAttribute('aria-labelledby') && el.getAttribute('aria-labelledby')?.trim() !== '';
      const hasTitle = el.hasAttribute('title') && el.getAttribute('title')?.trim() !== '';

      if (!hasLabel && !hasLabelledBy && !hasTitle) {
        el.setAttribute('aria-label', 'Loading');
      }
    }
  }

  private fixKeyboardAccessibility(el: HTMLElement): void {
    if (el.hasAttribute('onclick') && !el.hasAttribute('tabindex')) {
      el.setAttribute('tabindex', '0');
    }
  }

  private fixFocusOrder(el: HTMLElement): void {
    if (el.tagName === 'INPUT') {
      if (!el.hasAttribute('tabindex')) {
        el.setAttribute('tabindex', '0');
      } else {
        const tabindexValue = parseInt(el.getAttribute('tabindex') || '0', 10);
        if (tabindexValue > 0) {
          el.setAttribute('tabindex', '0');
        }
      }
    }

  }

  private fixHeadings(el: HTMLElement): void {
    if (el.classList.contains('section-title') && !el.querySelector('h2')) {
      const h2 = this.document.createElement('h2');
      h2.textContent = el.textContent?.trim() || 'Section';
      el.innerHTML = '';
      el.appendChild(h2);
    }
  }

  private fixSensoryCharacteristics(el: HTMLElement): void {
    if (el.classList.contains('color-only') && !el.hasAttribute('aria-label')) {
      el.setAttribute('aria-label', 'status indicator');
    }
  }

  private fixMeaningfulSequence(el: HTMLElement): void {
    if (el.tagName === 'MAIN' && !el.hasAttribute('role')) {
      el.setAttribute('role', 'main');
    }
  }

  private fixMissingLandmarks(el: HTMLElement): void {
    if (el.closest('[role="region"], main,[role="main"],header,[role="banner"],footer,[role="contentinfo"],nav,[role="navigation"],aside,[role="complementary"],section[role="region"]')) {
      return;
    }

    const tag = el.tagName.toLowerCase();
    const isContainer = tag === 'div' || tag === 'section' || tag === 'form';
    const isInlineOrList =
      tag === 'span' || tag === 'li' || tag === 'ul' || tag === 'ol' ||
      tag === 'label' || tag === 'p' || /^h[1-6]$/.test(tag);

    const isWidgetInternal =
      el.classList.contains('p-paginator-current') ||
      el.matches('p-dropdown, .p-dropdown, .p-calendar, .p-autocomplete, .p-datepicker') ||
      el.classList.contains('p-overlay') ||
      el.classList.contains('p-datepicker-title') ||
      el.classList.contains('p-datepicker-calendar-container') ||
      el.classList.contains('spinner-container') ||
      el.classList.contains('p-toggleable-content') ||
      el.classList.contains('p-accordion-content') ||
      el.classList.contains('p-tabview-panels') ||
      el.classList.contains('p-dialog-content');

    let label: string | null = null;

    const heading = el.querySelector(':scope h1, :scope h2, :scope h3, :scope h4, :scope h5, :scope h6') as HTMLElement | null;
    if (heading && (heading.textContent || '').trim() !== '') {
      if (!heading.id) heading.id = `lm-h-${Math.random().toString(36).slice(2, 8)}`;
      label = heading.textContent!.trim();
    } else {
      if (el.matches('.flex.align-items-center')) {
        label = el.textContent?.trim() || 'Flex Container';
      } else if (el.matches('.py-3 span')) {
        label = el.textContent?.trim() || 'Section Content';
      } else if (el.matches('.font-bold')) {
        label = el.textContent?.trim() || 'Bold Text Section';
      } else if (el.matches('.p-datatable-wrapper')) {
        label = 'Data Table';
      } else if (el.matches('app-routing-statistic-summary')) {
        label = 'Routing Summary';
      } else if (el.tagName.toLowerCase() === 'p-table') {
        label = el.getAttribute('aria-label')?.trim() || 'Data Table';
      } else if (el.tagName.toLowerCase() === 'app-member-detail-header') {
        label = el.getAttribute('aria-label')?.trim() || 'Member Details';
      } else if (el.tagName.toLowerCase() === 'member-detail-ribbon') {
        label = el.getAttribute('aria-label')?.trim() || 'Member Detail Ribbon';
      } else if (el.classList.contains('p-panel-header')) {
        const titleSpan = el.querySelector('.p-panel-title') as HTMLElement | null;
        label = titleSpan?.textContent?.trim() || el.textContent?.trim() || 'Panel Header';
      } else if (el.classList.contains('header-2')) {
        label = el.textContent?.trim() || 'Section Header';
      } else if (el.classList.contains('my-4') && el.classList.contains('pr-0') && el.classList.contains('pt-0')) {
        label = 'Content Section';
      } else if (el.classList.contains('grid-row-gap')) {
        label = 'Grid Section';
      } else if (el.classList.contains('pl-8')) {
        label = el.textContent?.trim() || 'Indented Section';
      } else if (tag === 'form') {
        label = el.getAttribute('aria-label')?.trim() || 'Form Section';
      } else if (el.classList.contains('pb-6')) {
        label = 'Content Block';
      } else if (el.classList.contains('pb-4')) {
        label = el.textContent?.trim() || 'Paragraph Section';
      } else if (el.classList.contains('pb-2')) {
        label = el.textContent?.trim() || 'Paragraph Section';
      } else if (el.classList.contains('pb-1')) {
        label = el.textContent?.trim() || 'Paragraph Section';
      } else if (tag === 'ul' && el.classList.contains('pl-4')) {
        label = 'List Section';
      } else if (el.classList.contains('ng-sidebar__content')) {
        label = 'Sidebar Content';
      } else if (el.classList.contains('p-panel')) {
        const title = el.querySelector('.p-panel-header .p-panel-title') as HTMLElement | null;
        label = title?.textContent?.trim() || 'Panel';
      } else if (tag === 'div') {
        label = this.getLabelFromText(el) || this.getLabelFromClass(el) || null;
      }
    }

    if (!label) return;

    const key = label.trim().toLowerCase();
    let final = label.trim();
    if (this.globalLandmarkLabels.has(key)) {
      const count = (this.globalLandmarkLabels.get(key) as number) + 1;
      this.globalLandmarkLabels.set(key, count);
      final = `${final} (${count})`;
    } else {
      this.globalLandmarkLabels.set(key, 1);
    }

    if (!isContainer || isInlineOrList || isWidgetInternal) return;

    if (heading) {
      const dupCount = (this.globalLandmarkLabels.get(key) as number);
      if (dupCount > 1) {
        el.setAttribute('aria-label', final);
        el.removeAttribute('aria-labelledby');
      } else {
        el.setAttribute('aria-labelledby', heading.id);
      }
    } else {
      el.setAttribute('aria-label', final);
      el.removeAttribute('aria-labelledby');
    }

    if (tag === 'form') {
      if (!el.hasAttribute('role')) el.setAttribute('role', 'form');
    } else if (tag === 'section') {
    } else {
      el.setAttribute('role', 'region');
    }
  }

  // Tightened heading-parent logic
  private fixHeadingParentLandmark(el: HTMLElement): void {
    if (
      el.matches('h1, h2, h3, h4, h5, h6') &&
      !el.closest('[role="region"], main,[role="main"],header,footer,nav,[role="navigation"],aside,[role="complementary"]')
    ) {
      const parent = el.parentElement;
      if (parent && !parent.hasAttribute('role')) {
        const pt = parent.tagName.toLowerCase();
        if (pt === 'div' || pt === 'section') {
          if (!el.id) el.id = `hd-${Math.random().toString(36).slice(2, 8)}`;
          const base = (el.textContent || '').trim();
          const key = base.toLowerCase();
          let final = base;
          if (this.globalLandmarkLabels.has(key)) {
            const count = (this.globalLandmarkLabels.get(key) as number) + 1;
            this.globalLandmarkLabels.set(key, count);
            final = `${final} (${count})`;
            parent.setAttribute('aria-label', final);
            parent.removeAttribute('aria-labelledby');
          } else {
            this.globalLandmarkLabels.set(key, 1);
            parent.setAttribute('aria-labelledby', el.id);
          }
          if (pt === 'div') parent.setAttribute('role', 'region');
        }
      }
    }
  }

  private fixInfoAndRelationships(el: HTMLElement): void {
    if (el.tagName === 'SECTION' && !el.hasAttribute('role')) {
      el.setAttribute('role', 'region');
    }
    if (el.tagName === 'NAV' && !el.hasAttribute('role')) {
      el.setAttribute('role', 'navigation');
    }
  }

  private fixInvalidRoleOnLabels(element: HTMLElement): void {
    if (element.tagName === 'LABEL' && element.getAttribute('role') === 'region') {
      element.removeAttribute('role');
    }
  }

  private ensureUniqueLandmarks(root: HTMLElement): void {
    const landmarks = root.querySelectorAll(
      '[role="region"], [role="main"], [role="navigation"], [role="banner"], [role="contentinfo"]'
    );

    landmarks.forEach((landmark) => {
      const el = landmark as HTMLElement;
      let label: string | null = null;

      const labelledBy = el.getAttribute('aria-labelledby');
      if (labelledBy) {
        const headerEl = document.getElementById(labelledBy);
        const headerText = headerEl?.textContent?.trim();
        if (headerText && headerText.length > 0) {
          label = headerText;
        }
      }

      if (!label) {
        const aria = el.getAttribute('aria-label');
        if (aria && aria.trim().length > 0) {
          label = aria.trim();
        }
      }

      if (!label) {
        label = this.getLabelFromClass(el) || this.getLabelFromText(el) || 'Section';
      }

      const key = label.trim().toLowerCase();
      let final = label.trim();
      if (this.globalLandmarkLabels.has(key)) {
        const count = (this.globalLandmarkLabels.get(key) as number) + 1;
        this.globalLandmarkLabels.set(key, count);
        final = `${final} (${count})`;
      } else {
        this.globalLandmarkLabels.set(key, 1);
      }

      el.setAttribute('aria-label', label);
      el.removeAttribute('aria-labelledby');
    });
  }

  /** Helper to derive label from first class name */
  private getLabelFromClass(el: HTMLElement): string {
    const firstClass = el.className.split(' ')[0];
    return firstClass ? firstClass.replace(/[-_]/g, ' ') : '';
  }

  /** Helper to derive label from text content */
  private getLabelFromText(el: HTMLElement): string {
    const text = (el.textContent || '').trim();
    return text || '';
  }

  private fixHeadingOrder(el: HTMLElement): void {
    if (el.matches('h1, h2, h3, h4, h5, h6')) {
      const previousHeading = el.previousElementSibling?.closest('h1,h2,h3,h4,h5,h6');
      let correctLevel = 2;
      if (previousHeading) {
        const prevLevel = parseInt(previousHeading.tagName.replace('H', ''), 10);
        correctLevel = Math.min(prevLevel + 1, 6);
      }

      el.setAttribute('role', 'heading');
      el.setAttribute('aria-level', correctLevel.toString());
    }
  }

  private fixInvalidFormRoles(el: HTMLElement): void {
    if (el.tagName === 'FORM' && el.getAttribute('role') === 'region') {
      el.setAttribute('role', 'form'); // or remove role if aria-label exists
    }
  }

  private fixInvalidTabindex(el: HTMLElement): void {
    if (el.hasAttribute('tabindex')) {
      const tabindexValue = parseInt(el.getAttribute('tabindex')!, 10);
      if (!isNaN(tabindexValue) && tabindexValue > 0) {
        el.setAttribute('tabindex', '0');
      }
    }
  }

  private fixInvalidToggleableContentRoles(el: HTMLElement): void {
    if (el.classList.contains('p-toggleable-content')) {
      el.removeAttribute('role');
      el.setAttribute('role', 'group');
      const header = el.closest('.p-panel')?.querySelector('.p-panel-header .p-panel-title') as HTMLElement | null;
      if (header && !el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', header.textContent?.trim() || 'Panel Content');
      }
    }
  }


}